/**
 * BRXWrapperOverlay
 *
 * Self-contained loading overlay primitive: progress ring, heading/message
 * setters, warmup animation, error/retry state, and reveal/hide logic.
 *
 * Usage:
 *   var overlay = window.BRXWrapperOverlay.create(sectionRoot);
 *   overlay.showWarmup(function onWarmupComplete() { ... });
 *   overlay.setHeading('Almost ready');
 *   overlay.showError({ heading, message, onRetry: function() {...} });
 *   overlay.hide();
 */
(function () {
    var WARMUP_MS = 5000;

    function create(root) {
        var overlayEl       = root ? root.querySelector('[data-intake-overlay]')        : null;
        var progressValueEl = root ? root.querySelector('[data-intake-progress-value]') : null;
        var progressBarEl   = root ? root.querySelector('[data-intake-progress-bar]')   : null;
        var headingEl       = root ? root.querySelector('[data-intake-status-heading]') : null;
        var messageEl       = root ? root.querySelector('[data-intake-status-message]') : null;
        var retryBtnEl      = root ? root.querySelector('[data-intake-overlay-retry]')  : null;

        var circleRadius        = 48;
        var circleCircumference = 2 * Math.PI * circleRadius;
        var progressRaf         = null;
        // `hidden` gates the overlay's own visibility (hide()/showWarmup()).
        // `animating` gates only the warmup RAF loop, kept separate so
        // showError() can stop the animation without re-hiding an overlay
        // it just needs to (re)reveal.
        var hidden              = false;
        var animating           = false;
        var currentOnRetry      = null;

        if (progressBarEl) {
            progressBarEl.style.strokeDasharray  = String(circleCircumference);
            progressBarEl.style.strokeDashoffset = String(circleCircumference);
        }

        function setHeading(text) { if (headingEl) headingEl.textContent = text; }
        function setMessage(text) { if (messageEl) messageEl.textContent = text; }

        function setProgress(value) {
            var next = Math.max(0, Math.min(100, Math.round(value)));
            if (progressValueEl) progressValueEl.textContent = next + '%';
            if (progressBarEl) {
                var offset = circleCircumference - (circleCircumference * next / 100);
                progressBarEl.style.strokeDashoffset = String(offset);
            }
        }

        function hideRetryButton() {
            if (!retryBtnEl) return;
            retryBtnEl.classList.add('is-hidden');
            retryBtnEl.disabled = false;
        }

        function hide() {
            hidden    = true;
            animating = false;
            if (progressRaf) { cancelAnimationFrame(progressRaf); progressRaf = null; }
            hideRetryButton();
            if (overlayEl) {
                overlayEl.classList.add('is-hidden');
                overlayEl.setAttribute('aria-busy', 'false');
            }
        }

        // showWarmup can be called more than once (e.g. after "Resume intake").
        // It resets hidden state and restarts the animation.
        function showWarmup(onComplete) {
            hidden    = false;
            animating = true;
            hideRetryButton();
            if (overlayEl) {
                overlayEl.classList.remove('is-hidden');
                overlayEl.setAttribute('aria-busy', 'true');
            }

            var start = performance.now();
            setProgress(0);

            function tick(now) {
                if (!animating) return;
                var elapsed = now - start;
                var pct = (elapsed / WARMUP_MS) * 100;

                if (pct >= 100) {
                    setProgress(100);
                    if (typeof onComplete === 'function') onComplete();
                    // Only update copy if still animating (not revealed/errored)
                    if (animating) {
                        setHeading('Almost ready');
                        setMessage('Finalizing your intake\u2026');
                    }
                    return;
                }

                setProgress(pct);
                setHeading("It's time to step up your game in the bedroom.");
                setMessage('Answer a few questions to be matched with the right physician.');
                progressRaf = requestAnimationFrame(tick);
            }

            progressRaf = requestAnimationFrame(tick);
        }

        // showError surfaces a failure state with a retry action. It must
        // work even if the overlay was never shown or was already hidden
        // (e.g. the "Resume intake" skipOverlay path hides it up front),
        // so it explicitly un-hides rather than assuming showWarmup ran.
        function showError(options) {
            var opts = options || {};
            hidden    = false;
            animating = false;
            if (progressRaf) { cancelAnimationFrame(progressRaf); progressRaf = null; }
            if (overlayEl) {
                overlayEl.classList.remove('is-hidden');
                overlayEl.setAttribute('aria-busy', 'false');
            }
            setHeading(opts.heading || 'We\u2019re having trouble loading');
            setMessage(opts.message || 'Please try again.');
            currentOnRetry = typeof opts.onRetry === 'function' ? opts.onRetry : null;
            if (retryBtnEl) {
                retryBtnEl.textContent = opts.retryLabel || 'Try again';
                retryBtnEl.disabled = false;
                retryBtnEl.classList.remove('is-hidden');
            }
        }

        // Bound once so repeated showError() calls don't stack listeners.
        if (retryBtnEl) {
            retryBtnEl.addEventListener('click', function () {
                if (!currentOnRetry || retryBtnEl.disabled) return;
                retryBtnEl.disabled = true;
                setMessage('Retrying\u2026');
                currentOnRetry();
            });
        }

        return { showWarmup, setHeading, setMessage, setProgress, hide, showError };
    }

    window.BRXWrapperOverlay = { create: create };
})();
